import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import dayjs from "dayjs";
import {
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import { Form } from "antd";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import CommonDatePicker from "@/common/elements/DatePicker";
import TimePicker from "@/common/elements/TimePicker";
import TextField from "@/common/elements/InputElements/TextField";
import {
  cleanObject,
  getFormattedDayjsObjectToString,
  getOptionsFromRefList,
  getRefListNameByID,
  isNonEmptyVariable,
  objectToArray,
  toQueryString,
} from "@/util/common_function";
import JailIncidentInvolvedInmateInformation from "./JailIncidentInvolvedInmateInformation";
import JailIncidentInvolvedReportInformation from "./JailIncidentInvolvedReportInformation";
import JailIncidentClassViolationDetail from "./JailIncidentClassViolationDetail";
import JailIncidenSanctionInformation from "./JailIncidenSanctionInformation";
import JailIncidentInmateAdmitsGuilt from "./JailIncidentInmateAdmitsGuilt";
import JailIncidenHearingInformation from "./JailIncidenHearingInformation";
import JailIncidentIReviewHearings from "./JailIncidentIReviewHearings";
import Administrator from "@/components/JailIncidents/Administrator";
import Supervisor from "@/components/JailIncidents/Supervisor";
import { MODULES_LIST } from "@/util/common_variable";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import DataTable from "@/common/elements/DataTable";
import Modal from "@/common/elements/Modal";
import { useSelector } from "react-redux";

const JailIncidentsInvolvedDetail = forwardRef(
  (
    {
      isModify = false,
      data = null,
      editingIndex = null,
      reviewHearingData,
      setReviewHearingData,
      setApprovalSection,
    },
    ref,
  ) => {
    const { bookingsFieldsEnabled } = useSelector((state) => state?.bookings);
    const [involvementsForm] = Form.useForm();
    const [inmateSearchData, setInmateSearchData] = useState([]);
    const [inmateSearchAPILoading, setInmateSearchAPILoading] = useState(false);
    const [showInmateListModal, setShowInmateListModal] = useState(false);
    const [selectInmateDetails, setSelectInmateDetails] = useState(null);
    const [showBasicInfo, setShowBasicInfo] = useState(false);
    const [showTopBar, setShowTopBar] = useState(false);
    const [isFieldEnable, setisFieldEnable] = useState(bookingsFieldsEnabled);
    const [isInmateAvail, setisInmateAvail] = useState(false);

    const hearingStartDateTime = Form.useWatch(
      "startDateTime",
      involvementsForm,
    );

    useImperativeHandle(ref, () => ({
      form: involvementsForm,
      selectInmateDetails: selectInmateDetails,
    }));

    useEffect(() => {
      if (isModify) {
        if (isNonEmptyVariable(editingIndex)) {
          setShowBasicInfo(false);
          setShowTopBar(true);
        } else {
          setShowBasicInfo(true);
          setShowTopBar(false);
        }

        setisFieldEnable(true);
      } else {
        setShowBasicInfo(false);
        setShowTopBar(true);
        setisFieldEnable(false);
      }
    }, [isModify]);

    useEffect(() => {
      if (isNonEmptyVariable(hearingStartDateTime)) {
        if (
          !isNonEmptyVariable(
            involvementsForm.getFieldValue("hearingDueDateTime"),
          )
        ) {
          involvementsForm.setFieldValue(
            "hearingDueDateTime",
            dayjs(hearingStartDateTime).add(5, "day"),
          );
        }
      }
    }, [hearingStartDateTime]);

    const tableHeaders = [
      { key: "name", text: "Name" },
      { key: "bookingNumber", text: "Booking Number" },
      { key: "arrestDate", text: "Arrested Date" },
    ];

    const disciplineRequired = Form.useWatch(
      "disciplineRequired",
      involvementsForm,
    );

    const majorOptionsAll = getOptionsFromRefList(
      "JailIncidentsMajorViolations",
      false,
      false,
      true,
    );

    const minorOptionsAll = getOptionsFromRefList(
      "JailIncidentsMinorViolations",
      false,
      false,
      true,
    );

    const classIOptionsAll = getOptionsFromRefList(
      "JailIncidentsclassIViolations",
      false,
      false,
      true,
    );

    const sanctionOptionsAll = getOptionsFromRefList(
      "JailIncidentsSanctions",
      false,
      false,
      true,
    );

    const combineDateTime = (date, time) => {
      return date && time ? dayjs(`${date} ${time}`, "MM/DD/YYYY HH:mm") : null;
    };

    useEffect(() => {
      if (isNonEmptyVariable(data)) {
        involvementsForm.setFieldsValue({
          ...data,
          jailIncidentsInvolvedInmateReviewHearings:
            data?.jailIncidentsInvolvedInmateReviewHearings || [],
          disciplineRequired: isNonEmptyVariable(data?.disciplineRequired)
            ? data?.disciplineRequired?.trim()
            : null,
          supervisorName: getStaffName(data?.supervisorStaffID),
          administratorName: getStaffName(data?.administratorStaffID),
          supervisorDateTime: combineDateTime(
            data?.supervisorDate,
            data?.supervisorTime,
          ),
          hearingDateTime: combineDateTime(
            data?.hearingDate,
            data?.hearingTime,
          ),
          startDateTime: combineDateTime(
            data?.hearingStartDate,
            data?.hearingStartTime,
          ),
          hearingDueDateTime: combineDateTime(
            data?.hearingDueDate,
            data?.hearingDueTime,
          ),
          administratorDateTime: combineDateTime(
            data?.administratorDate,
            data?.administratorTime,
          ),
          penaltyStartDateTime: combineDateTime(
            data?.penaltyStartDate,
            data?.penaltyStartTime,
          ),
          penaltyEndDateTime: combineDateTime(
            data?.penaltyEndDate,
            data?.penaltyEndTime,
          ),
          acceptDateTime: combineDateTime(data?.acceptDate, data.acceptTime),
        });

        if (isNonEmptyVariable(data?.bookingID)) {
          onSearchInmate({
            bookingId: data?.bookingID,
          });
          setisInmateAvail(true);
        }
      }
    }, [data]);

    useEffect(() => {
      if (inmateSearchData?.length) {
        if (!isInmateAvail) {
          setShowInmateListModal(true);
        } else {
          handleInmateListRowClick(inmateSearchData?.[0]);
        }
      }
    }, [inmateSearchData]);

    const getStaffName = (staffID) => {
      return getRefListNameByID("Staff", staffID, true) || "N/A";
    };

    const onSearchInmate = (value) => {
      const newValue = {
        ...value,
        searchType: "Starts With",
      };
      const prePayload = cleanObject(newValue);
      let filter = [];
      if (isNonEmptyVariable(prePayload)) {
        filter = objectToArray(prePayload, "filterKey", "value");
      }

      getFolderSearchData(filter);
    };

    const getFolderSearchData = (filter) => {
      let url = API_ENDPOINTS.facilityLog.getInmate;
      const obj = {
        page: 0,
        size: 15,
      };
      const urlParam = toQueryString(obj);
      if (isNonEmptyVariable(urlParam)) {
        url += "?" + urlParam;
      }

      setInmateSearchAPILoading(true);
      api
        .post(url, filter)
        .then((response) => {
          const rowsData = response?.data?.map((jsd) => {
            return {
              ...jsd,
              name: `${jsd?.lname}, ${jsd?.fname} ${isNonEmptyVariable(jsd?.mname) ? ", " + jsd?.mname : "" || ""}`,
            };
          });

          setInmateSearchData(() => rowsData);
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
          } else {
            console.error("An error occurred:", error);
          }
        })
        .finally(() => {
          setInmateSearchAPILoading(false);
        });
    };

    const handleInmateListRowClick = (data) => {
      if (isNonEmptyVariable(data)) {
        setSelectInmateDetails(data);
        setShowInmateListModal(false);
        setShowBasicInfo(() => false);
        setShowTopBar(() => true);
        setisInmateAvail(false);
      }
    };

    const updateForm = (data) => {
      setApprovalSection((prev) => ({
        ...(prev || {}),
        ...data,
      }));
    };

    return (
      <>
        {/* Inmate Information and Report Information are always shown */}
        <JailIncidentInvolvedInmateInformation
          selectedInmate={selectInmateDetails}
          setSelectInmateDetails={setSelectInmateDetails}
          showBasicInfo={showBasicInfo}
          setShowBasicInfo={setShowBasicInfo}
          from={MODULES_LIST.JAIL_INCIDENTS}
          onSave={onSearchInmate}
          showTopBar={showTopBar}
          setShowTopBar={setShowTopBar}
          inmateSearchAPILoading={inmateSearchAPILoading}
          isFieldEnable={isFieldEnable}
        />
        <Form form={involvementsForm}>
          <JailIncidentInvolvedReportInformation
            data={involvementsForm}
            disciplineRequired={disciplineRequired}
            isFieldEnable={isFieldEnable}
          />

          {isNonEmptyVariable(disciplineRequired) &&
            (disciplineRequired === "Discipline Required" ||
              disciplineRequired === "Verbal Warning") && (
              <>
                <JailIncidentClassViolationDetail
                  involvementsForm={involvementsForm}
                  majorOptionsAll={majorOptionsAll}
                  minorOptionsAll={minorOptionsAll}
                  classIOptionsAll={classIOptionsAll}
                  isFieldEnable={isFieldEnable}
                />
                {disciplineRequired === "Discipline Required" && (
                  <JailIncidenSanctionInformation
                    involvementsForm={involvementsForm}
                    sanctionOptionsAll={sanctionOptionsAll}
                    isFieldEnable={isFieldEnable}
                  />
                )}
                <JailIncidentInmateAdmitsGuilt
                  data={data}
                  isFieldEnable={isFieldEnable}
                />
                <JailIncidenHearingInformation
                  data={involvementsForm}
                  isFieldEnable={isFieldEnable}
                />
              </>
            )}

          {/* Review Hearings Section is always shown */}
          <JailIncidentIReviewHearings
            data={reviewHearingData}
            setReviewHearingData={setReviewHearingData}
            isFieldEnable={isFieldEnable}
          />

          {(disciplineRequired === "Discipline Required" ||
            disciplineRequired === "Verbal Warning") && (
            <>
              {/* Approval Section */}
              <StyledSectionHeading marginTop="20px">
                <span>Approval Information</span>
              </StyledSectionHeading>
              <StyledGridRow>
                <StyledGridCol marginBottom="15px">
                  <FormItem
                    label="Approved?"
                    layout="horizontal"
                    labelPostion="label-right"
                    name="approved"
                    valuePropName="checked"
                  >
                    <Checkbox disabled={!isFieldEnable} />
                  </FormItem>
                </StyledGridCol>
              </StyledGridRow>
              <Supervisor
                data={data}
                parentFieldEnable={isModify}
                from={MODULES_LIST.JAIL_INCIDENTS}
                involvementsForm={involvementsForm}
                updateForm={updateForm}
              />
              <Administrator
                data={data}
                parentFieldEnable={isModify}
                from={MODULES_LIST.JAIL_INCIDENTS}
                involvementsForm={involvementsForm}
                updateForm={updateForm}
              />
            </>
          )}
        </Form>

        <Modal
          width={"50%"}
          header="Select One"
          show={showInmateListModal}
          handleCancel={() => setShowInmateListModal(false)}
          onHide={() => setShowInmateListModal(false)}
          footerActionsNeed={false}
        >
          <DataTable
            rowUniqueId={"bookingNumber"}
            headers={tableHeaders}
            width="100%"
            className="table-striped"
            selectionColumnVisible={false}
            data={inmateSearchData || []}
            onRowClick={handleInmateListRowClick}
          />
        </Modal>
      </>
    );
  },
);

export default JailIncidentsInvolvedDetail;
